import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, IconButton,
} from '@mui/material';
import LogoLoader from '../components/Common/LogoLoader';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { getBots, createBot, deleteBot } from '../api/botApi';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import DeleteBotDialog from '../components/Common/DeleteBotDialog';

const Dashboard = () => {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const loadBots = async () => {
    try {
      const data = await getBots();
      setBots(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBots(); }, []);

  const handleCreate = async () => {
    if (!newBotName.trim()) return;
    setCreating(true);
    try {
      await createBot(newBotName.trim());
      toast.success('Bot created successfully!');
      setOpenCreate(false);
      setNewBotName('');
      loadBots();
    } catch (err) {
      toast.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (botId, botName) => {
    setDeleteTarget({ botId, botName });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteBot(deleteTarget.botId);
      toast.success('Bot deleted');
      setDeleteTarget(null);
      loadBots();
    } catch (err) {
      toast.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleBotClick = (botId) => {
    localStorage.setItem('selectedBotId', botId);
    navigate(`/app/bot/${botId}`);
  };

  if (loading) {
    return <LogoLoader message="Loading your bots..." />;
  }

  return (
    <Box sx={{ p: 3, minHeight: 'calc(100vh - 60px)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Bot Hub</Typography>
          <Typography variant="subtitle1" sx={{ color: 'gray' }}>
            Create and manage your chatbots
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#6366f1' }}
        >
          Create Bot
        </Button>
      </Box>

      {bots.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <SmartToyIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#9ca3af', mb: 1 }}>No bots yet</Typography>
          <Typography variant="body2" sx={{ color: '#d1d5db', mb: 3 }}>
            Click "Create Bot" to get started
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {bots.map((bot) => (
            <Grid item xs={12} sm={6} md={4} key={bot._id}>
              <Paper
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.25s, transform 0.2s',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: '0 4px 16px rgba(99,102,241,0.12)', transform: 'translateY(-2px)' },
                  opacity: bot.status === 'inactive' ? 0.6 : 1,
                }}
                onClick={() => handleBotClick(bot._id)}
              >
                <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #f1f1f1' }}>
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: '10px',
                      background: bot.status === 'active'
                        ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                        : '#e5e7eb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <SmartToyIcon sx={{ color: '#fff', fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{bot.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                      Created {new Date(bot.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 11, color: bot.status === 'active' ? '#6366f1' : '#9ca3af', fontWeight: 500 }}>
                      {bot.status === 'active' ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDelete(bot._id, bot.name); }}
                    sx={{ color: '#d1d5db', '&:hover': { color: '#ef4444' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.15)' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SmartToyIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>Create New Bot</Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Give your chatbot a name to get started</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'rgba(249,250,251,0.8)', backdropFilter: 'blur(8px)', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151', mb: 1 }}>Bot Name</Typography>
            <TextField autoFocus fullWidth size="small" value={newBotName}
              onChange={(e) => setNewBotName(e.target.value)}
              placeholder="e.g. Support Bot, Sales Assistant..."
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', '&.Mui-focused fieldset': { borderColor: '#6366f1' } } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => setOpenCreate(false)} fullWidth variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e5e7eb', color: '#6b7280' }}>
            Cancel
          </Button>
          <Button onClick={handleCreate} fullWidth variant="contained"
            disabled={!newBotName.trim() || creating}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              boxShadow: '0 4px 15px rgba(99,102,241,0.4)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
              '&.Mui-disabled': { background: '#e5e7eb', color: '#9ca3af', boxShadow: 'none' } }}>
            {creating ? 'Creating...' : '✨ Create Bot'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />

      <DeleteBotDialog
        open={!!deleteTarget}
        botName={deleteTarget?.botName}
        deleting={deleting}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default Dashboard;
